import React, { useEffect, useRef, useState } from 'react';
import { Stack } from 'react-bootstrap';
import classnames from 'classnames';

import { PrivateCSDPAttachmentsModal } from '@components/Attachment';
import Button from '@components/Button';
import Chip from '@components/Chip';
import Input from '@components/Input';
import { getStaffReplyTranslation, StaffReplyTranslation } from '@components/TranslationDisplay';

import { useUserContext } from '@private-crm-app/user-context';
import { trackAddAttachment, trackSendMessage } from '@private-crm-app/utils';

import { S3_CRM_SIGNED_URL_FAILURE } from '@actions/s3';
import { S3_CRM_SIGNED_URL_SUCCESS } from '@actions/s3';

import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';
import {
  cleanMessageForProfile,
  formatMessageTimestamp,
  getAllMessagesForChat,
  MessageDirection,
} from '@utils/MessageProfileUtil';

import { getCRMSignedURL, getPrivateSubmissionMessages, sendPrivateSubmissionMessage } from '../requests/submission';

import './style.scss';

const cyberdyneValidator = new CyberdyneValidator();
const errMsgGen = new ErrorMessageGenerator();

// const LINK_INVALID_CODE = 404;
// const LINK_EXPIRED_CODE = 403;

const MESSAGE_TIMESTAMP_LABELS = {
  INBOUND: 'Sent on',
  OUTBOUND: 'Received on',
} as const;

const FORM_VALIDATION = {
  MAX_BODY_LENGTH: 500,
  BODY_FIELD_NAME: 'Text body',
} as const;

const PLACEHOLDER_TEXT = 'Type your message here';

interface MessageItemProps {
  message: any;
}

const renderLines = (text: string) =>
  text.split('\n').map((line, index) => (
    <div key={index} className="message-paragraph">
      {line.trim() || '\u00A0'}
    </div>
  ));

const MessageItem: React.FC<MessageItemProps> = ({ message }) => {
  const staffReplyTranslation = getStaffReplyTranslation(message);

  return (
    <Stack
      gap={1}
      className={classnames('submission-detail__communication__message', {
        inbound: message.type === MessageDirection.INBOUND,
        outbound: message.type === MessageDirection.OUTBOUND,
      })}
    >
      <span className="submission-detail__communication__timestamp">
        {MESSAGE_TIMESTAMP_LABELS[message.type]} {formatMessageTimestamp(message.createdAt)}
      </span>
      <div className="submission-detail__communication__message-body">
        {staffReplyTranslation ? (
          <StaffReplyTranslation
            original={renderLines(cleanMessageForProfile(staffReplyTranslation.sourceBody))}
            translatedText={cleanMessageForProfile(staffReplyTranslation.translatedBody)}
            languageCode={staffReplyTranslation.translationLanguage}
            translationLabel={staffReplyTranslation.translationLabel}
            format="text"
          />
        ) : (
          renderLines(message.body)
        )}
      </div>
    </Stack>
  );
};

export default function PrivateSubmissionCommunication({
  submission,
  readOnly = false,
  onMessagesLoaded,
}: {
  submission: ICustomerSubmissionResponse;
  readOnly?: boolean;
  onMessagesLoaded?: (hasMessages: boolean) => void;
}) {
  const { user } = useUserContext();

  const messageContainerRef = useRef(null);

  const customerInfo = submission.customerInfo;
  const isEmailNotificationPreferenceSelected = user?.notificationPreference?.includes(
    NotificationPreferenceType.EMAIL
  );
  const isSMSNotificationPreferenceSelected = user?.notificationPreference?.includes(NotificationPreferenceType.SMS);

  const [body, setBody] = useState('');
  const [error, setError] = useState<IFieldValidation>({
    errorState: null,
    errorMessage: '',
  });
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [messages, setMessages] = useState<IEmailResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    getPrivateSubmissionMessages(String(submission.id))
      .then((fetched) => {
        const visibleMessages = fetched.filter((e) => e.recipientType !== RecipientType.FOLLOWER);
        setMessages(visibleMessages);
        onMessagesLoaded?.(getAllMessagesForChat(visibleMessages).length > 0);
      })
      .catch(() => {
        setMessages([]);
        onMessagesLoaded?.(false);
      })
      .finally(() => setIsLoading(false));
  }, [onMessagesLoaded, submission.id]);

  const messagesToShow = getAllMessagesForChat(messages);
  const hasMessages = messagesToShow && messagesToShow.length > 0;

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const onSend = async () => {
    if (validateForm()) {
      trackSendMessage(user?.name, user?.email);
      const succeeded = await onMessageSend(getRequestFromState());
      if (succeeded) {
        setBody('');
        setAttachments([]);
        setError({
          errorState: null,
          errorMessage: '',
        });
      }
    }
  };
  const toggleAttachmentModal = () => {
    setIsAttachmentModalOpen((prevState) => !prevState);
  };

  const validateForm = () => {
    let isValid = true;
    let internalError = error;

    if (cyberdyneValidator.isEmptyString(body)) {
      internalError = {
        errorState: IValidationState.ERROR,
        errorMessage: errMsgGen.getMandatoryFieldMessage(FORM_VALIDATION.BODY_FIELD_NAME),
      };

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(body, FORM_VALIDATION.MAX_BODY_LENGTH)) {
      internalError = {
        errorState: IValidationState.ERROR,
        errorMessage: errMsgGen.getCharacterLimitMessage(
          FORM_VALIDATION.BODY_FIELD_NAME,
          FORM_VALIDATION.MAX_BODY_LENGTH
        ),
      };

      isValid = false;
    }

    setError(internalError);

    return isValid;
  };

  const getRequestFromState = (): ICRMMessageRequest => ({
    body,
    attachments,
    customerInfo: getCommunicationPreferenceData(),
  });

  const getCommunicationPreferenceData = (): ICRMCommunicationPreferenceInfo => {
    const { email, phoneNumber, notificationNote, id } = customerInfo;
    const notificationPreference: NotificationPreferenceType[] = [];

    if (isEmailNotificationPreferenceSelected) {
      notificationPreference.push(NotificationPreferenceType.EMAIL);
    }
    if (isSMSNotificationPreferenceSelected) {
      notificationPreference.push(NotificationPreferenceType.SMS);
    }

    return {
      id,
      email: email ? email.trim() || null : null,
      phoneNumber: phoneNumber ? phoneNumber.trim() || null : null,
      notificationPreference,
      notificationNote: notificationNote ? notificationNote.trim() || null : null,
    };
  };

  const onMessageSend = async (messageRequest: ICRMMessageRequest): Promise<boolean> => {
    try {
      const sentMessage = await sendPrivateSubmissionMessage(submission.id, messageRequest);
      try {
        const refreshedMessages = await getPrivateSubmissionMessages(String(submission.id));
        setMessages(refreshedMessages.filter((e) => e.recipientType !== RecipientType.FOLLOWER));
      } catch {
        // GET refresh failed — append the single returned message as a best-effort fallback
        setMessages((prev) => [...prev, sentMessage].filter((e) => e.recipientType !== RecipientType.FOLLOWER));
      }

      return true;
    } catch (err) {
      setError({
        errorState: IValidationState.ERROR,
        errorMessage: 'Failed to send your message. Please try again.',
      });

      return false;
    }
  };

  const onAttachmentRemove = (index: number) => (e: React.SyntheticEvent<any>) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const addAttachments = (newAttachments: IXerceCaseAttachmentRequest[]) => {
    const mappedAttachments = newAttachments.map((d) => ({
      fileName: d.fileName,
      fileURL: d.fileURL,
      fileSize: d.fileSize,
    }));

    trackAddAttachment(user?.name, user?.email, mappedAttachments[0].fileName);

    toggleAttachmentModal();
    setAttachments([...attachments, ...mappedAttachments]);
  };

  const renderAttachmentsModal = () => {
    const acceptedFileTypes = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];

    const getCRMSignedURLCaller = async (crmToken: string, operation: S3Operation, filename: string) => {
      try {
        const response = await getCRMSignedURL(crmToken, operation, filename);

        return {
          type: S3_CRM_SIGNED_URL_SUCCESS,
          response,
        };
      } catch (error) {
        return {
          type: S3_CRM_SIGNED_URL_FAILURE,
          error,
        };
      }
    };

    return (
      <PrivateCSDPAttachmentsModal
        title="Photos"
        show={isAttachmentModalOpen}
        onClose={toggleAttachmentModal}
        onAdd={addAttachments}
        agencyId={submission.agencyId}
        s3Operation={S3Operation.CRM_ISSUE_ATTACHMENT}
        acceptedFileTypes={acceptedFileTypes}
        hasEditableFields={false}
        getCRMSignedURL={getCRMSignedURLCaller}
        crmToken={submission.crmToken}
      />
    );
  };

  if (readOnly && !hasMessages && !isLoading) {
    return null;
  }

  return (
    <Stack className="submission-detail__communication">
      <h2 className="submission-detail__communication-label">Messages</h2>
      <Stack gap={2} className="submission-detail__communication__messages" ref={messageContainerRef}>
        {hasMessages ? messagesToShow.map((message) => <MessageItem key={message.id} message={message} />) : null}
      </Stack>
      {!readOnly && (
        <>
          <Input
            placeholder={PLACEHOLDER_TEXT}
            name="body"
            onChange={(e) => setBody(e.target.value)}
            field={{
              label: '',
              value: body,
              isRequired: false,
              type: CustomFieldType.TEXTAREA,
            }}
            error={error}
          />
          <div className={classnames('submission-detail__communication__attachments')}>
            {attachments.map((attachment, index) => (
              <Chip key={attachment.fileName} onClose={onAttachmentRemove(index)}>
                {attachment.fileName}
              </Chip>
            ))}
          </div>
          <div className={classnames('submission-detail__communication__form-actions')}>
            <Button
              bsStyle={ButtonStyle.DEFAULT}
              content="Add Attachment"
              onClick={toggleAttachmentModal}
              aria-label="Add attachment to message"
            />
            <Stack gap={2} direction="horizontal">
              <Button
                bsStyle={ButtonStyle.DEFAULT}
                content="Cancel"
                onClick={() => setBody('')}
                aria-label="Cancel message"
              />
              <Button bsStyle={ButtonStyle.PRIMARY} content="Send" onClick={onSend} aria-label="Send message" />
            </Stack>
          </div>
          {renderAttachmentsModal()}
        </>
      )}
    </Stack>
  );
}
